import React, { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import DataSelector from "../../Components/ManageApplications/DataSelector"
import { humanReadableTimePassed } from "../../Helper/helpers"
import styles from '../JobApplications/ManageApplications.module.css'

// ─── STATUS ───────────────────────────────────────────────────────────────────
function getStatus(item) {
    if (item.isDeleted === "1") return "deleted"
    if (item.is_hired === "1") return item.hasJoined === "1" ? "joined" : "hired"
    if (item.is_shortlisted === "1") return "shortlisted"
    if (item.is_rejected === "1") return "rejected"
    return "applied"
}

const STATUS = {
    applied:     { label: "Applied",     color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe" },
    shortlisted: { label: "Shortlisted", color: "#d97706", bg: "#fffbeb", border: "#fcd34d" },
    hired:       { label: "Hired",       color: "#15803d", bg: "#f0fdf4", border: "#86efac" },
    joined:      { label: "Joined",      color: "#0369a1", bg: "#f0f9ff", border: "#7dd3fc" },
    rejected:    { label: "Rejected",    color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
    deleted:     { label: "Deleted",     color: "#9ca3af", bg: "#f9fafb", border: "#e5e7eb" },
}

const SOURCE = {
    MOBILE_APP: { label: "App",   color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
    WEB_ADMIN:  { label: "Admin", color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd" },
}

function latestDate(items) {
    return Math.max(...items.map(i => new Date(i.applied_on).getTime()))
}
function oldestDate(items) {
    return Math.min(...items.map(i => new Date(i.applied_on).getTime()))
}

// ─── PURE HELPERS (all outside main component to avoid remount bug) ────────────
function Avatar({ name, pic, size = 32 }) {
    const initials = (name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
    if (pic) return <img src={pic} alt={name} className={styles.avatar} style={{ width: size, height: size }} />
    return <div className={styles.avatarInitials} style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}>{initials}</div>
}

function SourceBadge({ source }) {
    const cfg = SOURCE[source] || { label: source, color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' }
    return <span className={styles.sourceBadge} style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}>{cfg.label}</span>
}

function StatusPill({ item, large }) {
    const s = getStatus(item)
    const cfg = STATUS[s]
    return <span className={large ? styles.statusPillLg : styles.statusPill} style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}>{cfg.label}</span>
}

// ─── APP MINI CARD ─────────────────────────────────────────────────────────────
function AppMiniCard({ item, isSelected, onSelect }) {
    return (
        <div className={`${styles.appCard} ${isSelected ? styles.appCardSelected : ''}`} onClick={() => onSelect(item)}>
            <div className={styles.cardAvatarCol}>
                <Avatar name={item.stylist_name} pic={item.applicantProfilePic} size={28} />
            </div>
            <div className={styles.cardBody}>
                <div className={styles.cardTopRow}>
                    <span className={styles.cardName}>{item.stylist_name?.trim() || '—'}</span>
                    <SourceBadge source={item.applied_source} />
                </div>
                <div className={styles.cardCompany}>{item.company_name?.trim()}</div>
                <div className={styles.cardMeta}>
                    <StatusPill item={item} />
                    {item.applicantGender && <span className={styles.genderDot}>{item.applicantGender === 'FEMALE' ? '♀' : '♂'}</span>}
                    {item.applicantAge && <span className={styles.ageBadge}>{item.applicantAge}y</span>}
                    <span className={styles.cardTime} title={item.applied_on}>{humanReadableTimePassed(item.applied_on)}</span>
                </div>
            </div>
        </div>
    )
}

// ─── GROUP ROW (left panel) ────────────────────────────────────────────────────
function GroupRow({ group, isSelected, onSelect }) {
    const statuses = group.items.map(getStatus)
    const hasHired = statuses.some(s => s === 'hired' || s === 'joined')
    const hasRejected = statuses.some(s => s === 'rejected')
    return (
        <div className={`${styles.groupRow} ${isSelected ? styles.groupRowSelected : ''}`} onClick={() => onSelect(group)}>
            <div className={styles.groupLabel}>
                <span className={styles.groupCount}>{group.items.length}</span>
                <div className={styles.groupNameStack}>
                    <span className={styles.groupName}>{group.label}</span>
                    {group.sublabel && <span className={styles.groupSublabel}>{group.sublabel}</span>}
                </div>
            </div>
            <div className={styles.groupMeta}>
                {hasHired && <span className={styles.tagHired}>Hired</span>}
                {hasRejected && <span className={styles.tagRejected}>Rej</span>}
                <span className={styles.groupArrow}>›</span>
            </div>
        </div>
    )
}

// ─── MIDDLE PANEL — GROUP APPS LIST ───────────────────────────────────────────
function GroupAppsPanel({ group, groupBy, selectedAppId, onSelectApp }) {
    const typeLabel = { candidate: 'Candidate', store: 'Store', job: 'Job' }[groupBy] || groupBy
    const statusCounts = group.items.map(getStatus).reduce((acc, s) => { acc[s] = (acc[s] || 0) + 1; return acc }, {})
    return (
        <div className={styles.middlePanel}>
            <div className={styles.middlePanelHeader}>
                <div className={styles.middlePanelTag}>{typeLabel} · {group.items.length}</div>
                <div className={styles.middlePanelName}>{group.label}</div>
                {group.sublabel && <div className={styles.middlePanelSublabel}>{group.sublabel}</div>}
                <div className={styles.middlePanelStats}>
                    {Object.entries(statusCounts).map(([s, count]) => {
                        const cfg = STATUS[s]
                        return <span key={s} className={styles.statChip} style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}>{count} {cfg.label}</span>
                    })}
                </div>
            </div>
            <div className={styles.middlePanelList}>
                {group.items.map(item => (
                    <AppMiniCard key={item.applicationId} item={item} isSelected={selectedAppId === item.applicationId} onSelect={onSelectApp} />
                ))}
            </div>
        </div>
    )
}

// ─── RIGHT PANEL — APP DETAIL ──────────────────────────────────────────────────
function AppDetailPanel({ item }) {
    const InfoRow = ({ label, value }) => {
        if (!value || value === '0000-00-00 00:00:00' || value === '0000-00-00') return null
        return (
            <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{label}</span>
                <span className={styles.infoValue}>{value}</span>
            </div>
        )
    }

    return (
        <div className={styles.rightContent}>
            <div className={styles.detailActions}>
                <a href={`/manage-application/${item.applicationId}`} target="_blank" rel="noreferrer" className={styles.openFullBtn}>
                    Open Full Application ↗
                </a>
            </div>

            <div className={styles.detailBlock}>
                <div className={styles.candidateHead}>
                    <Avatar name={item.stylist_name} pic={item.applicantProfilePic} size={52} />
                    <div className={styles.candidateHeadInfo}>
                        <div className={styles.candidateName}>{item.stylist_name?.trim() || '—'}</div>
                        <div className={styles.candidateSub}>
                            {item.stylist_contact}
                            {item.applicantGender && <span> · {item.applicantGender === 'FEMALE' ? '♀ Female' : '♂ Male'}</span>}
                            {item.applicantAge && <span> · {item.applicantAge} yrs</span>}
                        </div>
                        <div className={styles.candidateId}>{item.stylist_ID}</div>
                    </div>
                </div>
                <div className={styles.detailBadges}>
                    <StatusPill item={item} large />
                    <SourceBadge source={item.applied_source} />
                    {item.applicationCreatingExecutiveIdentifier && (
                        <span className={styles.execBadge}>by {item.applicationCreatingExecutiveIdentifier}</span>
                    )}
                </div>
            </div>

            <div className={styles.detailBlock}>
                <div className={styles.blockTitle}>Application</div>
                <InfoRow label="Application ID" value={item.applicationId} />
                <InfoRow label="Applied On" value={item.applied_on} />
                <InfoRow label="Job ID" value={item.job_id} />
                {item.applicantPincode && <InfoRow label="Pincode" value={item.applicantPincode} />}
            </div>

            <div className={styles.detailBlock}>
                <div className={styles.blockTitle}>Store</div>
                <InfoRow label="Company" value={item.company_name?.trim()} />
                <InfoRow label="Store ID" value={item.store_id} />
                {item.vender_contact && <InfoRow label="Contact" value={item.vender_contact} />}
                {item.vender_email && <InfoRow label="Email" value={item.vender_email} />}
            </div>

            <div className={styles.detailBlock}>
                <div className={styles.blockTitle}>Status Timeline</div>
                <InfoRow label="Applied" value={item.applied_on} />
                {item.is_shortlisted === "1" && <InfoRow label="Shortlisted" value={item.shortlisted_on} />}
                {item.is_hired === "1" && <InfoRow label="Hired" value={item.hired_on} />}
                {item.hasJoined === "1" && <InfoRow label="Joined" value={item.joinedOnDate} />}
                {item.is_rejected === "1" && <InfoRow label="Rejected" value={item.rejected_on} />}
                {item.is_unhired === "1" && <InfoRow label="Unhired" value={item.unhiredOn} />}
            </div>

            {item.coverLetter && (
                <div className={styles.detailBlock}>
                    <div className={styles.blockTitle}>Cover Letter</div>
                    <div className={styles.coverLetter}>{item.coverLetter}</div>
                </div>
            )}
        </div>
    )
}

// ─── FILTER DRAWER ────────────────────────────────────────────────────────────
function FilterDrawer({ filters, onChange, onClear, onClose, activeCount }) {
    const set = (key, val) => onChange({ ...filters, [key]: val })
    return (
        <div className={styles.drawerOverlay} onClick={onClose}>
            <div className={styles.drawerPanel} onClick={e => e.stopPropagation()}>
                <div className={styles.drawerHeader}>
                    <span className={styles.drawerTitle}>Filters</span>
                    <button className={styles.drawerClose} onClick={onClose}>✕</button>
                </div>
                <div className={styles.drawerBody}>
                    <div className={styles.drawerField}>
                        <label className={styles.drawerLabel}>Search</label>
                        <input
                            className={styles.searchInput}
                            placeholder="Name, phone, store, job ID, exec..."
                            value={filters.search}
                            onChange={e => set('search', e.target.value)}
                        />
                    </div>
                    <div className={styles.drawerField}>
                        <label className={styles.drawerLabel}>Status</label>
                        <div className={styles.chipRow}>
                            {[['active', 'Active'], ['all', 'All'], ['applied', 'Applied'], ['shortlisted', 'Shortlisted'], ['hired', 'Hired'], ['joined', 'Joined'], ['rejected', 'Rejected']].map(([v, l]) => (
                                <button key={v} className={`${styles.chip} ${filters.status === v ? styles.chipActive : ''}`} onClick={() => set('status', v)}>{l}</button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.drawerField}>
                        <label className={styles.drawerLabel}>Source</label>
                        <div className={styles.chipRow}>
                            {[['all', 'All'], ['MOBILE_APP', 'Mobile App'], ['WEB_ADMIN', 'Web Admin']].map(([v, l]) => (
                                <button key={v} className={`${styles.chip} ${filters.source === v ? styles.chipActive : ''}`} onClick={() => set('source', v)}>{l}</button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.drawerField}>
                        <label className={styles.drawerLabel}>Gender</label>
                        <div className={styles.chipRow}>
                            {[['all', 'All'], ['MALE', '♂ Male'], ['FEMALE', '♀ Female']].map(([v, l]) => (
                                <button key={v} className={`${styles.chip} ${filters.gender === v ? styles.chipActive : ''}`} onClick={() => set('gender', v)}>{l}</button>
                            ))}
                        </div>
                    </div>
                </div>
                {activeCount > 0 && (
                    <div className={styles.drawerFooter}>
                        <button className={styles.clearAllBtn} onClick={() => { onClear(); onClose() }}>
                            Clear All Filters ({activeCount})
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const DEFAULT_FILTERS = { search: '', status: 'active', source: 'all', gender: 'all' }

const SORT_OPTIONS = [
    { value: 'recent',   label: 'Most Recent' },
    { value: 'count',    label: 'Most Apps' },
    { value: 'oldest',   label: 'Oldest First' },
    { value: 'hired',    label: 'Has Hired' },
    { value: 'alpha',    label: 'A → Z' },
]

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const ManageApplications = () => {
    const [isDataSelectorOpen, setIsDataSelectorOpen] = useState(false)
    const [applicationData, setApplicationData] = useState([])
    const [selectedApp, setSelectedApp] = useState(null)
    const [selectedGroup, setSelectedGroup] = useState(null)
    const [filters, setFilters] = useState(DEFAULT_FILTERS)
    const [groupBy, setGroupBy] = useState('flat')
    const [groupSort, setGroupSort] = useState('recent')
    const [filterPanelOpen, setFilterPanelOpen] = useState(false)

    const handleLoadData = data => {
        setApplicationData(data)
        setSelectedApp(null)
        setSelectedGroup(null)
    }

    const handleSelectGroup = group => {
        setSelectedGroup(group)
        setSelectedApp(null)
    }

    const handleGroupByChange = v => {
        setGroupBy(v)
        setSelectedGroup(null)
        setSelectedApp(null)
    }

    // ─── FILTERED ─────────────────────────────────────────────────────────────
    const filtered = useMemo(() => applicationData.filter(item => {
        const status = getStatus(item)
        if (filters.status === 'active' && (status === 'rejected' || status === 'deleted')) return false
        if (filters.status !== 'all' && filters.status !== 'active' && status !== filters.status) return false
        if (filters.source !== 'all' && item.applied_source !== filters.source) return false
        if (filters.gender !== 'all' && item.applicantGender !== filters.gender) return false
        if (filters.search) {
            const q = filters.search.toLowerCase()
            const hit =
                (item.stylist_name || '').toLowerCase().includes(q) ||
                (item.stylist_contact || '').includes(q) ||
                (item.company_name || '').toLowerCase().includes(q) ||
                (item.job_id || '').toLowerCase().includes(q) ||
                (item.applicationCreatingExecutiveIdentifier || '').toLowerCase().includes(q)
            if (!hit) return false
        }
        return true
    }), [applicationData, filters])

    // ─── GROUPED + SORTED ─────────────────────────────────────────────────────
    const grouped = useMemo(() => {
        if (groupBy === 'flat') return null
        const map = new Map()
        filtered.forEach(item => {
            let key, label, sublabel
            if (groupBy === 'candidate') {
                key = item.stylist_ID
                label = `${item.stylist_name?.trim()} · ${item.stylist_contact}`
            } else if (groupBy === 'store') {
                key = item.store_id
                label = item.company_name?.trim()
            } else {
                key = item.job_id
                label = item.job_id
                sublabel = item.company_name?.trim()
            }
            if (!map.has(key)) map.set(key, { key, label, sublabel, items: [] })
            map.get(key).items.push(item)
        })
        const groups = Array.from(map.values())

        groups.sort((a, b) => {
            switch (groupSort) {
                case 'count':  return b.items.length - a.items.length
                case 'oldest': return oldestDate(a.items) - oldestDate(b.items)
                case 'alpha':  return a.label.localeCompare(b.label)
                case 'hired': {
                    const aH = a.items.some(i => getStatus(i) === 'hired' || getStatus(i) === 'joined') ? 1 : 0
                    const bH = b.items.some(i => getStatus(i) === 'hired' || getStatus(i) === 'joined') ? 1 : 0
                    return bH - aH || latestDate(b.items) - latestDate(a.items)
                }
                default: return latestDate(b.items) - latestDate(a.items) // 'recent'
            }
        })
        return groups
    }, [filtered, groupBy, groupSort])

    // keep currentGroup in sync when filters/sort change
    const currentGroup = useMemo(() => {
        if (!selectedGroup || !grouped) return null
        return grouped.find(g => g.key === selectedGroup.key) || null
    }, [selectedGroup, grouped])

    const activeFilterCount = [
        filters.status !== 'active',
        filters.source !== 'all',
        filters.gender !== 'all',
        filters.search !== '',
    ].filter(Boolean).length

    const isGroupedMode = grouped !== null

    return (
        <div className={styles.root}>
            <DataSelector
                isOpen={isDataSelectorOpen}
                filterWrapperToggler={() => setIsDataSelectorOpen(false)}
                appDataHandler={handleLoadData}
                selectedApplication={[]}
                selectApplicationHandler={() => {}}
            />

            {filterPanelOpen && (
                <FilterDrawer
                    filters={filters}
                    onChange={setFilters}
                    onClear={() => setFilters(DEFAULT_FILTERS)}
                    onClose={() => setFilterPanelOpen(false)}
                    activeCount={activeFilterCount}
                />
            )}

            {/* ── HEADER ──────────────────────────────────────────────── */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <Link to="/" className={styles.backLink}>← Back</Link>
                    <span className={styles.pageTitle}>Applications</span>
                    {applicationData.length > 0 && (
                        <span className={styles.totalBadge}>{applicationData.length} total</span>
                    )}
                </div>
                <div className={styles.headerRight}>
                    {applicationData.length > 0 && (
                        <button
                            className={`${styles.filterBtn} ${activeFilterCount > 0 ? styles.filterBtnActive : ''}`}
                            onClick={() => setFilterPanelOpen(true)}
                        >
                            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                        </button>
                    )}
                    <button className={styles.loadBtn} onClick={() => setIsDataSelectorOpen(true)}>Load Data</button>
                </div>
            </div>

            {applicationData.length === 0 ? (
                <div className={styles.emptyScreen}>
                    <div className={styles.emptyIcon}>📋</div>
                    <div className={styles.emptyTitle}>No data loaded</div>
                    <div className={styles.emptySubtitle}>Use "Load Data" to fetch applications</div>
                    <button className={styles.loadBtn} style={{ marginTop: 10 }} onClick={() => setIsDataSelectorOpen(true)}>Load Data</button>
                </div>
            ) : (
                <div className={styles.body}>

                    {/* ── COL 1: GROUP LIST / FLAT LIST ──────────────── */}
                    <div className={styles.leftPanel}>
                        <div className={styles.leftPanelTop}>
                            {/* Group by selector */}
                            <div className={styles.controlRow}>
                                <span className={styles.controlLabel}>Group by</span>
                                {[['flat', 'Flat'], ['candidate', 'Candidate'], ['store', 'Store'], ['job', 'Job']].map(([v, l]) => (
                                    <button
                                        key={v}
                                        className={`${styles.chip} ${groupBy === v ? styles.chipGroupActive : ''}`}
                                        onClick={() => handleGroupByChange(v)}
                                    >{l}</button>
                                ))}
                            </div>

                            {/* Sort options — only when grouped */}
                            {isGroupedMode && (
                                <div className={styles.controlRow}>
                                    <span className={styles.controlLabel}>Sort</span>
                                    {SORT_OPTIONS.map(({ value, label }) => (
                                        <button
                                            key={value}
                                            className={`${styles.chip} ${groupSort === value ? styles.chipSortActive : ''}`}
                                            onClick={() => setGroupSort(value)}
                                        >{label}</button>
                                    ))}
                                </div>
                            )}

                            <div className={styles.resultCount}>
                                {filtered.length} of {applicationData.length}
                                {grouped ? ` · ${grouped.length} groups` : ''}
                                {activeFilterCount > 0 && <span className={styles.filterHint}> · filtered</span>}
                            </div>
                        </div>

                        <div className={styles.listScroll}>
                            {filtered.length === 0 ? (
                                <div className={styles.emptyList}>No results match your filters</div>
                            ) : isGroupedMode ? (
                                grouped.map(g => (
                                    <GroupRow
                                        key={g.key}
                                        group={g}
                                        isSelected={currentGroup?.key === g.key}
                                        onSelect={handleSelectGroup}
                                    />
                                ))
                            ) : (
                                filtered.map(item => (
                                    <AppMiniCard
                                        key={item.applicationId}
                                        item={item}
                                        isSelected={selectedApp?.applicationId === item.applicationId}
                                        onSelect={setSelectedApp}
                                    />
                                ))
                            )}
                        </div>
                    </div>

                    {/* ── COL 2: GROUP APPS (only in grouped mode with group selected) ── */}
                    {isGroupedMode && currentGroup && (
                        <GroupAppsPanel
                            group={currentGroup}
                            groupBy={groupBy}
                            selectedAppId={selectedApp?.applicationId}
                            onSelectApp={setSelectedApp}
                        />
                    )}

                    {/* ── COL 3: APP DETAIL ──────────────────────────── */}
                    <div className={styles.rightPanel}>
                        {selectedApp ? (
                            <AppDetailPanel item={selectedApp} />
                        ) : (
                            <div className={styles.noneSelected}>
                                {isGroupedMode
                                    ? currentGroup
                                        ? 'Select an application from the group'
                                        : 'Select a group to explore'
                                    : 'Select an application from the list'
                                }
                            </div>
                        )}
                    </div>

                </div>
            )}
        </div>
    )
}

export default ManageApplications
